import type { GroupedOptionEntry } from '@/features/chart/lib/configs/options-config'

export interface StrikeSymbols {
  call?: string
  put?: string
}

export interface StrikeIndexRange {
  startIndex: number
  endIndex: number
}

/** Prevents a numeric index range from one option chain being applied to another. */
export function resolveStrikeRangeForKey(
  range: StrikeIndexRange,
  rangeKey: string,
  currentKey: string
): StrikeIndexRange {
  return rangeKey === currentKey ? range : { startIndex: 0, endIndex: -1 }
}

/**
 * Clamps a scroll-derived range to the actual strike collection. Before the first
 * layout measurement, render only a small initial window instead of mounting the
 * entire expiry (some OPRA chains contain hundreds of rows).
 */
export function clampStrikeIndexRange(
  range: StrikeIndexRange,
  strikeCount: number,
  initialRowCount: number
): StrikeIndexRange {
  if (strikeCount <= 0) return { startIndex: 0, endIndex: -1 }

  if (range.endIndex < range.startIndex) {
    return { startIndex: 0, endIndex: Math.min(strikeCount, initialRowCount) - 1 }
  }

  const startIndex = Math.min(Math.max(0, range.startIndex), strikeCount - 1)
  const endIndex = Math.min(Math.max(startIndex, range.endIndex), strikeCount - 1)
  return { startIndex, endIndex }
}

/**
 * Builds a strike -> {call, put} symbol map from `grouped.expires` entries — NOT from
 * `optionRows` (which starts empty and is only populated once a ticker actually
 * arrives). Sourcing the windowed subscription list from `rows` would be circular: no
 * subscription -> no ticker -> `rows` stays empty -> the windowed list stays empty ->
 * still no subscription.
 */
export function buildStrikeSymbolMap(entries: GroupedOptionEntry[]): Map<number, StrikeSymbols> {
  const map = new Map<number, StrikeSymbols>()
  for (const entry of entries) {
    const current = map.get(entry.strike) ?? {}
    if (entry.type === 'C') current.call = entry.symbol
    else current.put = entry.symbol
    map.set(entry.strike, current)
  }
  return map
}

/**
 * Scroll position -> the strike index range that should be subscribed. `headerOffset`
 * is the sticky header height above the row list (same convention as the ATM
 * auto-scroll effect's `rowsContainerRef.offsetTop`).
 */
export function computeVisibleStrikeRange(params: {
  scrollTop: number
  clientHeight: number
  headerOffset: number
  rowHeight: number
  bufferRows: number
}): { startIndex: number; endIndex: number } {
  const relativeScrollTop = Math.max(0, params.scrollTop - params.headerOffset)
  const startIndex = Math.max(
    0,
    Math.floor(relativeScrollTop / params.rowHeight) - params.bufferRows
  )
  const endIndex =
    Math.floor((relativeScrollTop + params.clientHeight) / params.rowHeight) + params.bufferRows
  return { startIndex, endIndex }
}

/**
 * Best-guess strike index to center a WINDOWED subscription on before the real ATM
 * price is known. Real chains list strikes roughly symmetric around spot, so the
 * middle of the sorted list is far more likely to be a liquid, actively-quoted
 * region than an arbitrary/stale scroll position — landing there reliably produces
 * a ticker snapshot, which is the only source of `underlyingPrice`
 * (use-option-chain-table.ts's processTickers), breaking the circular "need price to
 * pick a range, need a subscribed symbol to learn price" dependency that otherwise
 * leaves a wide/windowed chain subscribed to a silent corner indefinitely.
 */
export function estimateFallbackStrikeIndex(strikeCount: number): number {
  return strikeCount > 0 ? Math.floor(strikeCount / 2) : -1
}

/**
 * Expands the strikes inside `range` into their call/put symbols (deduped), then hard-
 * caps the result at `maxSymbols` — a large viewport (tall window + wide buffer) can
 * make the windowed result itself exceed the budget, so this always truncates as a
 * final guarantee regardless of how big the range turned out to be.
 */
export function collectWindowedOptionSymbols(params: {
  sortedStrikes: number[]
  strikeSymbols: Map<number, StrikeSymbols>
  range: { startIndex: number; endIndex: number }
  maxSymbols: number
}): string[] {
  const { sortedStrikes, strikeSymbols, range, maxSymbols } = params
  if (range.endIndex < range.startIndex || maxSymbols <= 0 || sortedStrikes.length === 0) return []

  const startIndex = Math.max(0, range.startIndex)
  const endIndex = Math.min(sortedStrikes.length - 1, range.endIndex)
  if (endIndex < startIndex) return []

  const indices = Array.from(
    { length: endIndex - startIndex + 1 },
    (_, offset) => startIndex + offset
  )
  const centerIndex = (startIndex + endIndex) / 2
  // When the requested viewport exceeds the stream budget, consume strikes nearest
  // the viewport center first. The previous low-to-high iteration followed by slice()
  // silently discarded the high side and could leave visible rows unsubscribed.
  indices.sort((a, b) => Math.abs(a - centerIndex) - Math.abs(b - centerIndex) || a - b)

  const symbols = new Set<string>()
  for (const index of indices) {
    const strike = sortedStrikes[index]
    const entry = strikeSymbols.get(strike)
    if (entry?.call && symbols.size < maxSymbols) symbols.add(entry.call)
    if (entry?.put && symbols.size < maxSymbols) symbols.add(entry.put)
    if (symbols.size >= maxSymbols) break
  }
  return [...symbols]
}
